import React, { useEffect, useState } from 'react'
import { useLazyQuery, useQuery } from '@apollo/client'
import { ClipboardList } from 'lucide-react'
import { Button as ButtonComponent } from '@/components/ui/button'
import { AUDIENCE_READINESS } from '@/graphql/account-signals-queries'
import { GET_AUDIENCE_BUILDER_ENABLED } from '@/graphql/audience-hypothesis-queries'
import { ButtonProps } from '@/types'
import { CHECK_AGAIN_COOLDOWN_MS } from '@/components/mediaBuying/adCampaign/SignalPanel'

// Type cast for the Button component from JSX file
const Button = ButtonComponent as React.FC<ButtonProps>

interface ReadinessComponent {
    key: string
    label: string
    status: string
    value: string | null
    note: string | null
}

interface ReadinessEvent {
    event: string
    status: string
    count: number | null
    last_seen: string | null
}

interface ReadinessPath {
    key: string
    label: string
    status: string
    reason: string | null
}

interface ReadinessAudience {
    purpose: string
    kind: string
    label: string
    status: string
    approximate_count_lower: number | null
    message: string | null
    eligible: boolean | null
}

interface ReadinessSwitch {
    key: string
    label: string
    on: boolean | null
}

export interface AudienceReadiness {
    available: boolean
    message: string | null
    checked_at: string | null
    objective: string
    level: string | null
    level_label: string | null
    level_reason: string | null
    level_rules: string[]
    events_window_days: number
    components: ReadinessComponent[]
    events: ReadinessEvent[]
    audiences: ReadinessAudience[]
    min_seed_size: number
    switches: ReadinessSwitch[]
    paths: ReadinessPath[]
    summary_text: string
}

export const READINESS_NOT_AVAILABLE = 'Audience readiness is not available right now.'

const COMPONENT_STATUS_TEXT: Record<string, string> = {
    unknown: 'Unknown',
    not_available: 'Not available',
    not_applicable: 'Not applicable',
}

export const PATH_STATUS_TEXT: Record<string, string> = {
    available: 'Available',
    waiting: 'Not yet',
    off: 'Turned off',
    not_available: 'Not available',
    per_campaign: 'Checked per campaign',
    unknown: 'Unknown',
}

const PATH_STATUS_STYLES: Record<string, string> = {
    available: 'tw:text-green-400',
    waiting: 'tw:text-gray-200',
    off: 'tw:text-gray-200',
    not_available: 'tw:text-gray-200',
    per_campaign: 'tw:text-gray-200',
    unknown: 'tw:text-gray-200',
}

const AUDIENCE_STATUS_TEXT: Record<string, string> = {
    ready: 'Ready',
    populating: 'Being filled by Meta',
    not_created: 'Not created yet',
    not_available: 'Cannot be built',
    too_small: 'Too small yet',
    flagged: 'Flagged by Meta',
    unusable: 'Not usable',
    being_prepared: 'Being prepared',
    unknown: 'Unknown',
}

/** A component's value, or the plain word for why there is none. */
export const componentValue = (component: ReadinessComponent): string =>
    component.status === 'known' && component.value !== null
        ? component.value
        : (COMPONENT_STATUS_TEXT[component.status] ?? 'Unknown')

export const eventValue = (event: ReadinessEvent): string =>
    event.status === 'known' && event.count !== null
        ? String(event.count)
        : (COMPONENT_STATUS_TEXT[event.status] ?? 'Unknown')

export const audienceValue = (audience: ReadinessAudience): string => {
    const status = AUDIENCE_STATUS_TEXT[audience.status] ?? 'Unknown'
    return audience.approximate_count_lower !== null && ['ready', 'too_small'].includes(audience.status)
        ? `${status}, about ${audience.approximate_count_lower.toLocaleString('en-US')} people`
        : status
}

const Row: React.FC<{ label: string; value: string; note?: string | null }> = ({ label, value, note }) => (
    <div className="tw:flex tw:flex-col tw:text-sm">
        <div className="tw:flex tw:justify-between tw:gap-4">
            <span className="tw:text-gray-200">{label}</span>
            <span className="tw:text-right">{value}</span>
        </div>
        {note && <span className="tw:text-xs tw:text-gray-200">{note}</span>}
    </div>
)

interface AudienceReadinessPanelProps {
    publisherId?: string | null
    campaignId?: string | null
    objective?: string | null
}

/**
 * Read-only overview of the licence's own data and the audience paths it allows. Shown only for licences with
 * AI audiences; nothing is read from Meta until the panel is opened.
 */
const AudienceReadinessPanel: React.FC<AudienceReadinessPanelProps> = ({ publisherId, campaignId, objective }) => {
    const [open, setOpen] = useState(false)
    const [showText, setShowText] = useState(false)
    const [coolingDown, setCoolingDown] = useState(false)
    const { data: builderData } = useQuery(GET_AUDIENCE_BUILDER_ENABLED, {
        variables: { campaignId },
        skip: !campaignId,
    })
    const [loadReadiness, { data, loading, error }] = useLazyQuery(AUDIENCE_READINESS, {
        fetchPolicy: 'network-only',
    })
    const canLoad = !!(publisherId && campaignId)

    useEffect(() => {
        if (open && canLoad) loadReadiness({ variables: { publisherId, campaignId, objective } })
    }, [open, canLoad, publisherId, campaignId, objective, loadReadiness])

    useEffect(() => {
        if (!coolingDown) return undefined
        const timer = setTimeout(() => setCoolingDown(false), CHECK_AGAIN_COOLDOWN_MS)
        return () => clearTimeout(timer)
    }, [coolingDown])

    if (builderData?.AudienceHypothesis?.audienceBuilderEnabled !== true) return null

    const checkAgain = () => {
        if (coolingDown || loading) return
        setCoolingDown(true)
        loadReadiness({ variables: { publisherId, campaignId, objective, refresh: true } })
    }

    if (!open) {
        return (
            <div className="tw:pt-2">
                <Button
                    variant="link"
                    className="tw:px-0 tw:gap-1"
                    onClick={() => {
                        setOpen(true)
                        setCoolingDown(true)
                    }}
                >
                    <ClipboardList className="tw:w-4 tw:h-4" />
                    Check audience readiness
                </Button>
            </div>
        )
    }

    const readiness: AudienceReadiness | undefined = data?.audienceReadiness

    return (
        <div
            className="tw:mt-4 tw:flex tw:flex-col tw:gap-3 tw:rounded-lg tw:bg-gray-500 tw:px-4 tw:py-3"
            data-testid="audience-readiness-panel"
        >
            <div className="tw:flex tw:items-center tw:justify-between">
                <span className="tw:text-sm tw:font-semibold">Audience readiness</span>
                {readiness?.available && (
                    <Button variant="link" className="tw:px-0" disabled={coolingDown || loading} onClick={checkAgain}>
                        Check again
                    </Button>
                )}
            </div>

            {!canLoad || error ? (
                <span className="tw:text-sm tw:text-gray-200">{READINESS_NOT_AVAILABLE}</span>
            ) : loading || !readiness ? (
                <span className="tw:text-sm tw:text-gray-200">Checking…</span>
            ) : !readiness.available ? (
                <span className="tw:text-sm tw:text-gray-200" data-testid="readiness-not-available">
                    {readiness.message || READINESS_NOT_AVAILABLE}
                </span>
            ) : (
                <>
                    <div className="tw:flex tw:flex-col tw:gap-1" data-testid="readiness-level">
                        <span className="tw:text-sm tw:font-semibold">{readiness.level_label}</span>
                        <span className="tw:text-sm">{readiness.level_reason}</span>
                    </div>

                    <ul className="tw:flex tw:flex-col tw:gap-2" aria-label="Audience paths">
                        {readiness.paths.map((path) => (
                            <li key={path.key} className="tw:flex tw:flex-col tw:text-sm" data-status={path.status}>
                                <div className="tw:flex tw:justify-between tw:gap-4">
                                    <span>{path.label}</span>
                                    <span className={PATH_STATUS_STYLES[path.status] ?? 'tw:text-gray-200'}>
                                        {PATH_STATUS_TEXT[path.status] ?? PATH_STATUS_TEXT.unknown}
                                    </span>
                                </div>
                                {path.reason && <span className="tw:text-xs tw:text-gray-200">{path.reason}</span>}
                            </li>
                        ))}
                    </ul>

                    <div className="tw:flex tw:flex-col tw:gap-1" aria-label="Readiness data">
                        {readiness.components.map((component) => (
                            <Row
                                key={component.key}
                                label={component.label}
                                value={componentValue(component)}
                                note={component.note}
                            />
                        ))}
                    </div>

                    <div className="tw:flex tw:flex-col tw:gap-1">
                        <span className="tw:text-sm tw:font-semibold">
                            Events in the last {readiness.events_window_days} days
                        </span>
                        <ul aria-label="Readiness events">
                            {readiness.events.map((event) => (
                                <li key={event.event} className="tw:flex tw:justify-between tw:text-sm">
                                    <span>{event.event}</span>
                                    <span>{eventValue(event)}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {readiness.audiences.length > 0 && (
                        <div className="tw:flex tw:flex-col tw:gap-1">
                            <span className="tw:text-sm tw:font-semibold">Audiences of this licence</span>
                            <ul aria-label="Readiness audiences">
                                {readiness.audiences.map((audience) => (
                                    <li
                                        key={`${audience.purpose}:${audience.kind}:${audience.label}`}
                                        className="tw:flex tw:justify-between tw:gap-4 tw:text-sm"
                                    >
                                        <span>{audience.label}</span>
                                        <span className="tw:text-right">{audienceValue(audience)}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="tw:flex tw:flex-col tw:gap-1">
                        <Button variant="link" className="tw:px-0 tw:self-start" onClick={() => setShowText(!showText)}>
                            {showText ? 'Hide as text' : 'Show as text'}
                        </Button>
                        {showText && (
                            <pre
                                className="tw:whitespace-pre-wrap tw:text-xs tw:text-gray-200"
                                data-testid="readiness-text"
                            >
                                {readiness.summary_text}
                            </pre>
                        )}
                    </div>
                </>
            )}
        </div>
    )
}

export default AudienceReadinessPanel
